/* format.js — Formate les valeurs du modèle normalisé en chaînes lisibles
 * (ne lit jamais le JSON brut). Expose self.MediaFormat. */
'use strict';

import FINDINGS from './findings.js';

function esc(s) {
  return String(s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

/* ---------- Formateurs unitaires (nombre normalisé -> texte) ---------- */

function bytes(b) {
  if (b == null || !isFinite(b)) return null;
  var u = ['o', 'Ko', 'Mo', 'Go', 'To'], i = 0;
  while (b >= 1000 && i < u.length - 1) { b /= 1000; i++; }
  return (i === 0 ? b : b.toFixed(2)) + ' ' + u[i];
}
function duration(s) {
  if (s == null || !isFinite(s)) return null;
  var h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  var out = [];
  if (h) out.push(h + ' h');
  if (m || h) out.push(m + ' min');
  out.push(sec + ' s');
  return out.join(' ');
}
function bitrate(b) {
  if (b == null || !isFinite(b)) return null;
  if (b >= 1e6) return (b / 1e6).toFixed(2) + ' Mb/s';
  if (b >= 1e3) return Math.round(b / 1e3) + ' kb/s';
  return b + ' b/s';
}
function sampling(hz) {
  if (hz == null || !isFinite(hz)) return null;
  return (hz / 1000).toLocaleString('fr-FR') + ' kHz';
}
function fps(f) {
  if (f == null || !isFinite(f)) return null;
  return f.toLocaleString('fr-FR') + ' i/s';
}
function bits(v) { return v == null ? null : v + ' bits'; }
function aspect(r) {
  if (r == null || !isFinite(r)) return null;
  if (Math.abs(r - 1.778) < 0.01) return '16:9';
  if (Math.abs(r - 1.333) < 0.01) return '4:3';
  if (Math.abs(r - 2.35) < 0.03) return '2.35:1 (scope)';
  if (Math.abs(r - 1.85) < 0.01) return '1.85:1';
  return r.toFixed(3);
}
function frMode(v) { return v === 'CFR' ? 'Constante (CFR)' : v === 'VFR' ? 'Variable (VFR)' : v; }
function brMode(v) { return v === 'CBR' ? 'Constant (CBR)' : v === 'VBR' ? 'Variable (VBR)' : v; }
function scan(v) { return v === 'progressive' ? 'Progressif' : v === 'interlaced' ? 'Entrelacé' : v; }
function bool(v) { return v === true ? 'Oui' : v === false ? 'Non' : null; }
function lang(l) {
  if (!l || l.raw == null) return null;
  return l.name ? l.name + ' (' + l.raw + ')' : l.raw;
}
function channels(t) {
  if (t.channels == null) return null;
  var lay = t.channelLayout;
  if (lay && lay.friendly && lay.friendly !== (t.channels + ' canaux')) {
    return t.channels + ' (' + lay.friendly + ')';
  }
  if (t.channels === 1) return '1 (mono)';
  if (t.channels === 2) return '2 (stéréo)';
  return t.channels + ' canaux';
}
function dims(t) {
  if (t.width == null || t.height == null) return null;
  var s = t.width + ' × ' + t.height + ' px';
  var std = stdRes(t.width, t.height);
  if (std) s += ' (' + std + ')';
  return s;
}
// Dimensions codées, si elles débordent l'image affichée.
function coded(t) {
  if (t.storedWidth == null && t.storedHeight == null) return null;
  var w = t.storedWidth != null ? t.storedWidth : t.width;
  var h = t.storedHeight != null ? t.storedHeight : t.height;
  if (w === t.width && h === t.height) return null;
  return w + ' × ' + h + ' px';
}

// 1080p, 720p… d'après la hauteur, bandes noires comprises.
var STD_HEIGHTS = [4320, 2160, 1440, 1080, 720, 576, 480, 360, 240];
function resLabel(t) {
  if (!t.height) return null;
  var h = t.height;
  for (var i = 0; i < STD_HEIGHTS.length; i++) {
    if (h >= STD_HEIGHTS[i] * 0.92) return STD_HEIGHTS[i] + (t.scanType === 'interlaced' ? 'i' : 'p');
  }
  return h + (t.scanType === 'interlaced' ? 'i' : 'p');
}

function shorten(v, max) {
  v = String(v);
  return v.length > max ? v.slice(0, max - 1).replace(/\s+\S*$/, '') + '…' : v;
}

// Ce qui distingue une piste des autres d'un coup d'œil.
function trackLabel(t) {
  var parts = [];
  var name = t.language && (t.language.name || t.language.raw);
  if (t.kind === 'video' || t.kind === 'image') {
    if (t.kind === 'video' && resLabel(t)) parts.push(resLabel(t));
    if (t.format) parts.push(t.format);
    if (t.kind === 'video' && t.frameRate) parts.push(fps(t.frameRate));
  } else {
    if (name) parts.push(name);
    if (t.format) parts.push(t.format);
    if (t.kind === 'audio') {
      if (t.channelLayout && t.channelLayout.friendly) parts.push(t.channelLayout.friendly);
      else if (t.channels) parts.push(channels(t));
    }
  }
  // pour la vidéo, le titre répète le nom de la source
  if (t.title && t.kind !== 'video') parts.push(shorten(t.title, 60));
  return parts.join(' · ') || t.format;
}

// « 10,01 s »
function seconds(v) {
  if (v == null) return null;
  return String(Math.round(v * 1000) / 1000).replace('.', ',') + ' s';
}

function stdRes(w, h) {
  if (w === 7680 && h === 4320) return '8K UHD';
  if (w === 4096 && h === 2160) return 'DCI 4K';
  if (w === 3840 && h === 2160) return '4K UHD';
  if (w === 2560 && h === 1440) return 'QHD 1440p';
  if (w === 1920 && h === 1080) return 'Full HD 1080p';
  if (w === 1280 && h === 720) return 'HD 720p';
  var mx = Math.max(w, h), mn = Math.min(w, h);
  if (mx >= 7680) return 'classe 8K';
  if (mx >= 3840) return 'classe 4K';
  if (mn >= 1080) return 'classe Full HD';
  return null;
}

/* ---------- Définition des lignes affichées par type ----------
 * Chaque entrée : [libellé, fonction (piste|conteneur) -> texte|null].
 * La fonction lit le modèle normalisé, pas le JSON brut. */

var CONTAINER_ROWS = [
  ['Conteneur', function (c) { return c.format; }],
  ['Profil', function (c) { return c.formatProfile; }],
  ['Durée', function (c) { return duration(c.duration); }],
  ['Débit global', function (c) { return bitrate(c.overallBitRate); }],
  ['Images/seconde', function (c) { return fps(c.frameRate); }],
  ['Application d’encodage', function (c) { return c.encodedApplication; }],
  ['Bibliothèque d’encodage', function (c) { return c.encodedLibrary; }],
  ['Date d’encodage', function (c) { return c.encodedDate; }],
  ['Date d’enregistrement', function (c) { return c.recordedDate; }],
  ['Titre', function (c) { return c.title; }],
  ['Film', function (c) { return c.movie; }],
  ['Album', function (c) { return c.album; }],
  ['Interprète', function (c) { return c.performer; }]
];

var TRACK_ROWS = {
  video: [
    ['Format', function (t) { return t.format; }],
    ['Profil', function (t) { return t.formatProfile; }],
    ['Niveau', function (t) { return t.formatLevel; }],
    ['Codec ID', function (t) { return t.codecId; }],
    ['Définition', dims],
    ['Définition codée', coded],
    ['Ratio d’affichage', function (t) { return aspect(t.displayAspectRatio); }],
    ['Images/seconde', function (t) { return fps(t.frameRate); }],
    ['Mode de cadence', function (t) { return frMode(t.frameRateMode); }],
    ['Débit', function (t) { return bitrate(t.bitRate); }],
    ['Profondeur', function (t) { return bits(t.bitDepth); }],
    ['Espace colorimétrique', function (t) { return t.colorSpace; }],
    ['Sous-échantillonnage chroma', function (t) { return t.chromaSubsampling; }],
    ['Position chroma', function (t) { return t.chromaPosition; }],
    ['Images de référence', function (t) { return t.refFrames; }],
    ['Type de balayage', function (t) { return scan(t.scanType); }],
    ['Format HDR', function (t) { return t.hdrFormat; }],
    ['Plage de couleur', function (t) { return t.colorRange; }],
    ['Primaires', function (t) { return t.colorPrimaries; }],
    ['Transfert (courbe)', function (t) { return t.transferCharacteristics; }],
    ['Matrice', function (t) { return t.matrixCoefficients; }],
    ['Affichage de mastering', function (t) { return t.masteringDisplay; }],
    ['Luminance de mastering', function (t) { return t.masteringLuminance; }],
    ['Éclairement crête (MaxCLL)', function (t) { return t.maxCLL; }],
    ['Éclairement moyen (MaxFALL)', function (t) { return t.maxFALL; }],
    ['Langue', function (t) { return lang(t.language); }],
    ['Titre', function (t) { return t.title; }],
    ['Par défaut', function (t) { return bool(t.default); }],
    ['Forcée', function (t) { return bool(t.forced); }]
  ],
  audio: [
    ['Format', function (t) { return t.format; }],
    ['Profil', function (t) { return t.formatProfile || t.formatFeatures; }],
    ['Codec ID', function (t) { return t.codecId; }],
    ['Canaux', channels],
    ['Disposition', function (t) { return t.channelLayout ? t.channelLayout.raw : null; }],
    ['Fréquence d’échantillonnage', function (t) { return sampling(t.samplingRate); }],
    ['Débit', function (t) { return bitrate(t.bitRate); }],
    ['Mode de débit', function (t) { return brMode(t.bitRateMode); }],
    ['Profondeur', function (t) { return bits(t.bitDepth); }],
    ['Langue', function (t) { return lang(t.language); }],
    ['Titre', function (t) { return t.title; }],
    ['Par défaut', function (t) { return bool(t.default); }],
    ['Forcée', function (t) { return bool(t.forced); }]
  ],
  text: [
    ['Format', function (t) { return t.format; }],
    ['Codec ID', function (t) { return t.codecId; }],
    ['Langue', function (t) { return lang(t.language); }],
    ['Titre', function (t) { return t.title; }],
    ['Forcés', function (t) { return bool(t.forced); }],
    ['Par défaut', function (t) { return bool(t.default); }]
  ],
  image: [
    ['Format', function (t) { return t.format; }],
    ['Définition', dims]
  ],
  menu: [],
  other: []
};

var KIND_LABEL = {
  general: 'Général', video: 'Vidéo', audio: 'Audio',
  text: 'Sous-titres', menu: 'Chapitres / Menu', image: 'Image', other: 'Autre'
};

/* ---------- Assemblage des lignes clés (label -> valeur) ---------- */

function collect(defs, obj) {
  var out = [];
  defs.forEach(function (d) {
    var v = d[1](obj);
    if (v != null && v !== '') out.push({ k: d[0], v: v });
  });
  return out;
}

// À la suite des lignes d'un sujet ; une mesure impossible affiche son motif.
function measuredRows(model, t) {
  var Kx = K();
  function mine(x) { return t ? x.subject.kind === t.kind && x.subject.ordinal === t.ordinal : x.subject.kind === 'container'; }
  var rows = (model.measured || []).filter(mine).map(function (v) {
    return { k: Kx.FIELDS[v.property] || v.property, v: valueText(v.property, v.value), origin: v.origin };
  });
  (model.absent || []).filter(mine).forEach(function (a) {
    rows.push({ k: Kx.FIELDS[a.property] || a.property, v: Kx.fill(Kx.LABELS.notMeasured, { reason: a.reason }), origin: 'measured' });
  });
  return rows;
}

// L'origine « déclarée » est portée par le bloc ; une ligne mesurée la porte.
function view(model) {
  var blocks = [];
  var note = K().LABELS.blockOrigin;
  blocks.push({
    kind: 'general',
    title: KIND_LABEL.general,
    sub: model.container.format,
    note: note,
    rows: collect(CONTAINER_ROWS, model.container).concat(measuredRows(model))
  });
  model.tracks.forEach(function (t) {
    if (t.kind === 'menu') return; // les chapitres sont présentés séparément
    var total = model.counts[t.kind] || 1;
    var title = KIND_LABEL[t.kind] || t.kind;
    if (total > 1) title += ' ' + t.ordinal;
    blocks.push({
      kind: t.kind,
      title: title,
      sub: trackLabel(t),
      note: note,
      rows: collect(TRACK_ROWS[t.kind] || [], t).concat(measuredRows(model, t))
    });
  });
  return blocks;
}

function summaryText(model, fileName) {
  var lines = [fileName || model.container.fileName || 'analyse', ''];
  view(model).forEach(function (b) {
    lines.push('== ' + b.title + (b.sub ? ' (' + b.sub + ')' : '') + ' ==');
    lines.push(b.note);
    b.rows.forEach(function (r) { lines.push(r.k + ' : ' + r.v + (r.origin ? ' (' + K().ORIGINS[r.origin] + ')' : '')); });
    lines.push('');
  });
  return lines.join('\n');
}

function coverImg(model) {
  var c = model.container && model.container.cover;
  if (!c || !c.present || !c.data) return '';
  var b64 = String(c.data).replace(/[^A-Za-z0-9+/=]/g, ''); // charset base64 uniquement
  var mime = /^\/9j\//.test(b64) ? 'image/jpeg' : /^iVBOR/.test(b64) ? 'image/png' : 'image/jpeg';
  return '<figure class="cover"><img alt="Pochette embarquée" src="data:' + mime + ';base64,' + b64 + '">' +
    '<figcaption>Pochette embarquée</figcaption></figure>';
}

function chaptersHtml(model) {
  var ch = model.chapters || [];
  if (!ch.length) return '';
  var items = ch.map(function (c) {
    return '<li><span class="ch-t mono">' + esc(c.time) + '</span><span>' + esc(c.title) + '</span></li>';
  }).join('');
  return '<div class="chapters-box"><h3>Chapitres · ' + ch.length + '</h3><ul>' + items + '</ul></div>';
}

/* ---------- Écarts : textes prêts à rendre ---------- */

// Mêmes formateurs que la fiche.
var VALUE_FMT = {
  duration: duration, bitRate: bitrate, overallBitRate: bitrate, fileSize: bytes,
  samplingRate: sampling, frameRate: fps, bitDepth: bits,
  displayAspectRatio: aspect, frameRateMode: frMode, bitRateMode: brMode, scanType: scan,
  width: function (v) { return v + ' px'; }, height: function (v) { return v + ' px'; },
  moovPosition: function (v) { return v === 'before-mdat' ? 'en tête, avant les données' : v === 'after-mdat' ? 'après les données (mdat)' : v; },
  keyframeInterval: seconds,
  avOffset: function (v) { return (v > 0 ? '+' : '') + seconds(v); },
  peakBitRate: bitrate,
  loudnessIntegrated: function (v) { return String(v).replace('.', ',') + ' LUFS'; },
  loudnessRange: function (v) { return String(v).replace('.', ',') + ' LU'; },
  truePeak: function (v) { return String(v).replace('.', ',') + ' dBTP'; },
  activeWidth: function (v) { return v + ' px'; },
  activeHeight: function (v) { return v + ' px'; },
  activeAspectRatio: function (v) { return String(Math.round(v * 100) / 100).replace('.', ',') + ':1'; },
  barsShare: function (v) { return Math.round(v * 100) + ' %'; }
};
function valueText(property, v) {
  var L = K().LABELS;
  if (v == null) return L.absent;
  if (v === true) return L.yes;
  if (v === false) return L.no;
  var f = VALUE_FMT[property];
  var out = f ? f(v) : null;
  return out != null ? String(out) : String(v);
}

function subjectLabel(subject, model) {
  if (!subject || subject.kind === 'container') return K().LABELS.file;
  var title = KIND_LABEL[subject.kind] || subject.kind;
  if ((model.counts[subject.kind] || 0) > 1) title += ' ' + subject.ordinal;
  return title;
}

function observedText(o, gap, model) {
  var Kx = K();
  var sameSubject = o.subject.kind === gap.subject.kind && o.subject.ordinal === gap.subject.ordinal;
  return (sameSubject ? '' : subjectLabel(o.subject, model) + ' — ') +
    (Kx.FIELDS[o.property] || o.property) + ' : ' + valueText(o.property, o.value) + ' — ' + Kx.ORIGINS[o.origin];
}

// Groupes non vides par priorité ; `hasProfile` choisit le texte du vide.
function gapsView(gaps, model, hasProfile) {
  var Kx = K();
  var groups = Kx.PRIORITY_ORDER.map(function (p) {
    return { priority: p, label: Kx.PRIORITIES[p], items: [] };
  });
  gaps.forEach(function (g, index) {
    var c = Kx.CATALOG[g.type] || {};
    var rule = g.emitter.kind === 'rule';
    var noAction = !(g.actions && g.actions.length) && g.correction !== 'impossible' ? c.noAction : null;
    var item = {
      index: index,
      severity: g.severity,
      severityLabel: Kx.SEVERITIES[g.severity],
      title: rule ? g.message : c.label,
      subject: subjectLabel(g.subject, model),
      domain: Kx.DOMAINS[g.domain] || null,
      expected: g.expected ? Kx.expectedText(g.expected, valueText) : null,
      observed: g.observed.map(function (o) { return observedText(o, g, model); }),
      explanation: rule
        ? Kx.fill(c.explanation, { rule: g.emitter.rule, profile: g.emitter.profile.id, version: g.emitter.profile.version || '?' })
        : c.explanation,
      correction: g.correction
        ? Kx.CORRECTIONS[g.correction] + (c.remedy && g.correction === 'impossible' ? ' — ' + c.remedy : '') + (noAction ? ' — ' + noAction : '')
        : null,
      confidence: g.confidence
        ? Kx.CONFIDENCE_LEVELS[g.confidence.level] + ' : ' + g.confidence.reasons.join(' ; ')
        : null,
      proof: g.proof && g.proof.structure ? {
        text: Kx.fill(Kx.LABELS.proofStructure, { name: g.proof.structure.name || '?', offset: g.proof.structure.offset.toLocaleString('fr-FR') }),
        offset: g.proof.structure.offset
      } : null
    };
    groups[Kx.PRIORITY_ORDER.indexOf(g.priority)].items.push(item);
  });
  return {
    total: gaps.length,
    title: Kx.LABELS.gaps,
    empty: hasProfile ? Kx.LABELS.emptyWithProfile : Kx.LABELS.emptyWithoutProfile,
    labels: Kx.LABELS,
    groups: groups.filter(function (g) { return g.items.length; })
  };
}

function gapCounts(gaps) {
  var out = { fix: 0, check: 0, optional: 0 };
  gaps.forEach(function (g) { out[g.priority]++; });
  return out;
}

// Règles qui ne sont pas des écarts, pour l'affichage replié.
function quietRules(report) {
  var out = { passed: [], unevaluable: [], notApplicable: [] };
  if (!report || !report.applicable.ok) return out;
  report.results.forEach(function (r) {
    var line = { message: r.message, observed: r.candidates ? r.observed.join(', ') : '' };
    if (r.status === 'pass') out.passed.push(line);
    else if (r.status === 'unevaluable') out.unevaluable.push(line);
    else if (r.status === 'not-applicable') out.notApplicable.push(line);
  });
  return out;
}

// -> { title, note, empty, items: [{ label, value, personal }] }
function privacyView(model) {
  var Kx = K(), L = Kx.LABELS;
  var items = (model.privacy || []).map(function (p) {
    var value = p.value;
    if (p.position) {
      value += ' — ' + Kx.fill(L.position, { lat: String(p.position.lat).replace('.', ','), lon: String(p.position.lon).replace('.', ','),
        alt: p.position.alt != null ? Kx.fill(L.altitude, { alt: String(p.position.alt).replace('.', ',') }) : '' });
    }
    return { label: Kx.PRIVACY_KINDS[p.kind], value: value, personal: Kx.PRIVACY_PERSONAL.indexOf(p.kind) >= 0 };
  });
  return { title: L.privacyTitle, note: L.privacyNote, empty: items.length ? null : L.privacyEmpty, items: items };
}

function K() { return FINDINGS; }

var api = {
  esc: esc, view: view, trackLabel: trackLabel, summaryText: summaryText,
  coverImg: coverImg, chaptersHtml: chaptersHtml,
  KIND_LABEL: KIND_LABEL,
  valueText: valueText, subjectLabel: subjectLabel,
  gapsView: gapsView, gapCounts: gapCounts, quietRules: quietRules,
  privacyView: privacyView,
  fmt: { bytes: bytes, duration: duration, bitrate: bitrate }
};
globalThis.MediaFormat = api;
export default api;
